import { NativeQSenseControlPlugin } from '@/plugins/NativeQSenseControl';
import { QSenseHubControlPlugin } from '@/plugins/QSenseHubControl';
import { Assignments, KinematicModelOptions } from '@/plugins/QSenseHubControl/types';

import { ISessionManager } from '../interfaces/ISessionManager';

/**
 * SessionManager bridges the Ionic/Capacitor layer with native session logic.
 *
 * It delegates session lifecycle calls (start/end) to the underlying
 * native plugin (either NativeQSenseControlPlugin or QSenseHubControlPlugin).
 * This ensures that measurement sessions initiated in the Ionic app
 * are tracked consistently across both JS and native contexts.
 *
 * Business Context:
 * - Invoked by Ionic UI or service layers to begin/end measurement sessions.
 * - Relies on native Capacitor plugins to persist session metadata and
 *   correlate recordings with session events.
 */
export class SessionManager implements ISessionManager {
  static getSessions(uid: string | undefined, clientId: string, organizationId: any) {
    throw new Error('Method not implemented.');
  }
  private plugin: NativeQSenseControlPlugin | QSenseHubControlPlugin;

  constructor(plugin: NativeQSenseControlPlugin | QSenseHubControlPlugin) {
    this.plugin = plugin;
  }

  /**
   * Starts a new measurement session.
   *
   * This method forwards the session configuration to the native plugin via
   * `initializePlugin`, which is responsible for storing session metadata
   * (sessionId, sessionStartTime) and preparing recording pipelines.
   *
   * @param sessionId - A unique identifier for the measurement session.
   * @param sessionStartTime - The timestamp marking the session start.
   * @param config - Configuration object including:
   *                 - KinematicModel: Options for the kinematic model in use.
   *                 - Whitelist: Array of sensor MAC addresses to connect to.
   *                 - Assignments: Array mapping sensor MAC addresses to segments.
   *
   * @throws Propagates any errors thrown by the native plugin during initialization.
   */
  public async start(
    sessionId: string,
    sessionStartTime: Date,
    config: {
      KinematicModel: KinematicModelOptions;
      Whitelist: string[];
      Assignments: Assignments[];
    },
    clientName: string,
    userId: string,
    clientId: string,
    IdToken: string,
  ): Promise<void> {
    // Note: sessionId and sessionStartTime are passed in config via
    // the native plugin implementation (e.g., as part of initializePlugin payload)
    await this.plugin.initializePlugin({
      SessionId: sessionId,
      SessionStartTime: sessionStartTime,
      ...config,
      ClientName: clientName,
      UserId: userId,
      ClientId: clientId,
      IdToken: IdToken,
    });
  }

  /**
   * Ends the current measurement session, clearing any stored state.
   *
   * Delegates to the native plugin's `resetSessionState`, which will
   * drop session metadata and ensure subsequent recordings are untagged
   * until a new session is started.
   *
   * @throws Propagates any errors thrown by the native plugin during reset.
   */
  public async end(): Promise<void> {
    await this.plugin.resetSessionState();
  }
}
